import { Badge } from "@shared/ui/badge";
import { Switch } from "@shared/ui/switch";
import { Edit, Trash2, Eye } from "lucide-react";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import React, { useEffect, useState } from 'react';
import { Button } from "@shared/ui/button";
import { Label } from "@shared/ui/label";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@shared/ui/select";

interface ServiceCenter {
  id: string;
  name: string;
  address: string;
  city: string;
  phone: string;
  email: string;
  areas: string[];
  subAreas: string[];
  premises: string[];
  services: string[];
  isActive: boolean;
  type: string;
}

interface ServiceCenterFilterPanelProps {
  currentFilters: {
    type: string;
  };
  onApplyFilters: (filters: { type?: string }) => void;
  onResetFilters: () => void;
  serviceTypes: string[];
}

const ServiceCenterFilterPanel: React.FC<ServiceCenterFilterPanelProps> = ({
  currentFilters,
  onApplyFilters,
  onResetFilters,
  serviceTypes,
}) => {
  const [tempFilters, setTempFilters] = useState({
    type: currentFilters.type || '',
  });

  useEffect(() => {
    setTempFilters({
      type: currentFilters.type || '',
    });
  }, [currentFilters]);

  const handleTypeChange = (value: string) => {
    setTempFilters((prev) => ({ ...prev, type: value === 'ALL_TYPES' ? '' : value }));
  };

  const handleApply = () => {
    const filtersToApply: { type?: string } = {};
    
    if (tempFilters.type) {
      filtersToApply.type = tempFilters.type;
    }
    
    onApplyFilters(filtersToApply);
  };

  const handleReset = () => {
    setTempFilters({ type: '' });
    onResetFilters();
  };

  return (
    <form onSubmit={e => { e.preventDefault(); handleApply(); }}>
      <div className="grid grid-cols-1 gap-6">
        <div className="space-y-2">
          <Label>Service Type</Label>
          <Select 
            value={tempFilters.type || 'ALL_TYPES'} 
            onValueChange={handleTypeChange}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select service type" />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectItem value="ALL_TYPES">All Types</SelectItem>
                {serviceTypes.map((type: string) => (
                  <SelectItem key={type} value={type}>
                    {type}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={handleReset}>
            Reset Filters
          </Button>
          <Button type="submit">
            Apply Filters
          </Button>
        </div>
      </div>
    </form>
  );
};

interface ServiceCenterTableProps {
  serviceCenters: ServiceCenter[];
  onView: (center: ServiceCenter) => void;
  onEdit: (center: ServiceCenter) => void;
  onDelete: (id: string) => void;
  onToggleActive: (center: ServiceCenter) => void;
  isUpdatingStatus?: boolean;
  // Search props
  searchPlaceholder?: string;
  searchParamName?: string;
  searchDebounceMs?: number;
  onSearch?: (searchTerm: string, paramName: "search_data") => void;
  // Filter props
  currentFilters?: {
    type: string;
  };
  onApplyFilters?: (filters: { type?: string }) => void;
  onResetFilters?: () => void;
  serviceTypes?: string[];
  // Pagination props
  pagination?: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
    startItem: number;
    endItem: number;
  };
  onPageChange?: (newPage: number) => void;
}

export const ServiceCenterTable = ({ 
  serviceCenters, 
  onView, 
  onEdit, 
  onDelete, 
  onToggleActive,
  isUpdatingStatus,
  searchPlaceholder,
  searchParamName,
  searchDebounceMs,
  onSearch,
  currentFilters,
  onApplyFilters,
  onResetFilters,
  serviceTypes = [],
  pagination,
  onPageChange 
}: ServiceCenterTableProps) => {
  // Define table columns
  const columns: TableColumn<ServiceCenter>[] = [
    {
      key: "name",
      header: "Name",
      sortable: true,
      render: (value: string) => (
        <span className="font-medium">{value}</span>
      ),
    },
    {
      key: "address",
      header: "Location",
      sortable: true,
    },
    {
      key: "phone",
      header: "Contact",
      sortable: true,
    },
    {
      key: "type",
      header: "Type",
      type: "badge",
      badgeConfig: {
        variant: "secondary",
      },
      sortable: true,
    },
    {
      key: "areas",
      header: "Areas",
      render: (value: string[], center: ServiceCenter) => (
        <div className="flex flex-wrap gap-1">
          {center.areas.slice(0, 2).map((area, index) => (
            <Badge key={index} variant="outline" className="text-xs">
              {area}
            </Badge>
          ))}
          {center.areas.length > 2 && (
            <Badge variant="outline" className="text-xs">
              +{center.areas.length - 2} more
            </Badge>
          )}
        </div>
      ),
      responsive: {
        hidden: "hidden lg:table-cell",
      },
    },
    {
      key: "subAreas",
      header: "Sub Areas",
      render: (value: string[], center: ServiceCenter) => (
        <div className="flex flex-wrap gap-1">
          {center.subAreas.slice(0, 2).map((subArea, index) => (
            <Badge key={index} variant="outline" className="text-xs">
              {subArea}
            </Badge>
          ))}
          {center.subAreas.length > 2 && (
            <Badge variant="outline" className="text-xs">
              +{center.subAreas.length - 2} more
            </Badge>
          )}
        </div>
      ),
      responsive: {
        hidden: "hidden xl:table-cell",
      },
    },
    {
      key: "premises",
      header: "Premises",
      render: (value: string[], center: ServiceCenter) => (
        <div className="flex flex-wrap gap-1">
          {center.premises.slice(0, 2).map((premise, index) => (
            <Badge key={index} variant="outline" className="text-xs">
              {premise}
            </Badge>
          ))}
          {center.premises.length > 2 && (
            <Badge variant="outline" className="text-xs">
              +{center.premises.length - 2} more
            </Badge>
          )}
        </div>
      ),
      responsive: {
        hidden: "hidden xl:table-cell",
      },
    },
    {
      key: "services",
      header: "Services",
      render: (value: string[], center: ServiceCenter) => (
        <div className="flex flex-wrap gap-1">
          {center.services.slice(0, 2).map((service, index) => (
            <Badge key={index} variant="outline" className="text-xs">
              {service}
            </Badge>
          ))}
          {center.services.length > 2 && (
            <Badge variant="outline" className="text-xs">
              +{center.services.length - 2} more
            </Badge>
          )}
        </div>
      ),
      responsive: {
        hidden: "hidden lg:table-cell",
      },
    },
    {
      key: "isActive",
      header: "Active",
      render: (value: boolean, center: ServiceCenter) => (
        <Switch
          checked={center.isActive}
          onCheckedChange={() => onToggleActive(center)}
        />
      ),
      width: "w-20",
    },
  ];

  // Define table actions
  const actions = [
    {
      label: "View",
      icon: <Eye className="h-4 w-4" />,
      onClick: (center: ServiceCenter) => onView(center),
      variant: "ghost" as const,
    },
    {
      label: "Edit",
      icon: <Edit className="h-4 w-4" />,
      onClick: (center: ServiceCenter) => onEdit(center),
      variant: "ghost" as const,
    },
    {
      label: "Delete",
      icon: <Trash2 className="h-4 w-4" />,
      onClick: (center: ServiceCenter) => onDelete(center.id),
      variant: "ghost" as const,
    },
  ];

  return (
    <GenericTable<ServiceCenter>
      data={serviceCenters}
      columns={columns}
      rowKey="id"
      actions={actions}
      actionsType="icons"
      searchPlaceholder={searchPlaceholder}
      searchParamName={searchParamName}
      searchDebounceMs={searchDebounceMs}
      onSearch={onSearch}
      filterPanel={
        currentFilters && onApplyFilters && onResetFilters ? (
          <ServiceCenterFilterPanel
            currentFilters={currentFilters}
            onApplyFilters={onApplyFilters}
            onResetFilters={onResetFilters}
            serviceTypes={serviceTypes}
          />
        ) : undefined
      }
      pagination={pagination}
      onPageChange={onPageChange}
      emptyMessage="No service centers found"
    />
  );
};